import React from 'react';
import styles from './Pagination.module.scss';

/**
 * Pagination component
 * Shows page navigation with prev/next and page numbers
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current page number (1-based)
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback when page changes
 */
export default function Pagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) {
        return null; // Don't show pagination if only one page
    }

    // Generate page numbers to show
    // Show first, last, and pages around current
    const getPageNumbers = () => {
        const pages = [];
        const showRange = 2; // Pages to show on each side of current

        // Always show first page
        pages.push(1);

        // Calculate start and end of middle range
        let start = Math.max(2, currentPage - showRange);
        let end = Math.min(totalPages - 1, currentPage + showRange);

        // Add ellipsis after first if needed
        if (start > 2) {
            pages.push('...');
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // Add ellipsis before last if needed
        if (end < totalPages - 1) {
            pages.push('...');
        }

        // Always show last page (if more than 1 page)
        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return pages;
    };

    const pages = getPageNumbers();

    return (
        <div className={styles.pagination}>
            {/* Previous button */}
            <button
                className={styles.pageButton}
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                &laquo;
            </button>

            {/* Page numbers */}
            {pages.map((page, index) => {
                if (page === '...') {
                    return (
                        <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                            ...
                        </span>
                    );
                }

                const isActive = page === currentPage;

                return (
                    <button
                        key={page}
                        className={`${styles.pageButton} ${isActive ? styles.active : ''}`}
                        onClick={() => onPageChange(page)}
                        disabled={isActive}
                    >
                        {page}
                    </button>
                );
            })}

            {/* Next button */}
            <button
                className={styles.pageButton}
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                &raquo;
            </button>
        </div>
    );
}
